import { WindowSize } from '@dustin-web/microsite-config/src/breakpoints'
import { CSSProperties, MutableRefObject } from 'react'

export type PositionType =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'top-free'
  | 'right'
  | 'bottom-right'
  | 'bottom-center'
  | 'bottom-left'
  | 'bottom-free'
  | 'left'

export type Position = {
  top?: number
  left?: number
}

export type PopoverPosition = {
  validPosition?: string
  top?: number
  left?: number
}

export const defaultPositions: PositionType[] = ['bottom-center', 'top-center', 'left', 'right']
const popoverOffset = 8

const checkPosition = (
  popoverRef: MutableRefObject<HTMLElement | null> | undefined,
  containerRef: MutableRefObject<HTMLElement | null>,
  position: PositionType
) => {
  if (containerRef.current && popoverRef?.current) {
    const popover = popoverRef.current.getBoundingClientRect()
    const container = containerRef.current.getBoundingClientRect()
    const containerRight = document.body.clientWidth - container.right
    const containerBottom = window.innerHeight - container.bottom
    const spaceTop = container.top > popover.height
    const spaceTopHalved = container.top > popover.height / 2 - container.height / 2
    const spaceRight = containerRight > popover.width
    const spaceRightHalved = containerRight > popover.width / 2 - container.width / 2
    const spaceBottom = containerBottom > popover.height
    const spaceBottomHalved = containerBottom > popover.height / 2 - container.height / 2
    const spaceLeft = container.left > popover.width
    const spaceLeftHalved = container.left > popover.width / 2 - container.width / 2

    switch (position) {
      case 'top-left':
        return spaceLeft && spaceTop
      case 'top-center':
        return spaceTop && spaceRightHalved && spaceLeftHalved
      case 'top-right':
        return spaceRight && spaceTop
      case 'right':
        return spaceRight && spaceTopHalved && spaceBottomHalved
      case 'bottom-right':
        return spaceRight && spaceBottom
      case 'bottom-center':
        return spaceBottom && spaceRightHalved && spaceLeftHalved
      case 'bottom-left':
        return spaceLeft && spaceBottom
      case 'left':
        return spaceLeft && spaceTopHalved && spaceBottomHalved
      case 'top-free':
      case 'bottom-free':
        return true
      default:
        return false
    }
  } else {
    return false
  }
}

type Props = {
  containerRef: MutableRefObject<HTMLElement | null>
  popoverRef: MutableRefObject<HTMLElement | null> | undefined
  positions?: PositionType[]
  overrideBestPlacement?: boolean
  positionOffset?: Position
  windowSize: WindowSize
}

export const getValidPosition = ({
  containerRef,
  popoverRef,
  positions = defaultPositions,
  overrideBestPlacement = false,
  positionOffset,
  windowSize,
}: Props) => {
  let validPosition = positions.find(position => checkPosition(popoverRef, containerRef, position))

  if (overrideBestPlacement && positions.length > 0) {
    // choose the last position in the list if none of the positions are valid
    if (!validPosition) {
      validPosition = positions[positions.length - 1]
    }
  } else {
    if (!validPosition) {
      validPosition = defaultPositions.find(position =>
        checkPosition(popoverRef, containerRef, position)
      )

      if (!validPosition) validPosition = defaultPositions[0]
    }
  }

  let position: Position | null = null
  let popoverStyle: CSSProperties | null = null
  let arrowStyle: CSSProperties | null = null

  if (containerRef.current && popoverRef?.current) {
    const popover = popoverRef.current.getBoundingClientRect()
    const container = containerRef.current.getBoundingClientRect()

    const parentOffset = getElementOffset(popoverRef.current)

    const [placement, alignment] = validPosition.split('-')
    let left = 0,
      top = 0

    switch (placement) {
      case 'top':
        top = container.top - (popover.height + popoverOffset)
        break
      case 'bottom':
        top = container.top + container.height + popoverOffset
        break
      case 'left':
        left = container.left - (popover.width + popoverOffset)
        break
      case 'right':
        left = container.left + container.width + popoverOffset
        break
    }

    if (placement == 'top' || placement == 'bottom') {
      switch (alignment) {
        case 'left':
          left = container.left - popover.width
          break
        case 'right':
          left = container.left + container.width
          break
        case 'center':
        default:
          left = container.left + container.width / 2 - popover.width / 2
          break
      }
    } else {
      top = container.top + container.height / 2 - popover.height / 2
    }

    top += (positionOffset?.top || 0) - parentOffset.top
    left += (positionOffset?.left || 0) - parentOffset.left

    const pagePadding = 16
    let targetWidth: number | null = null
    if (left < pagePadding) {
      left = pagePadding
    }
    if (left + popover.width > windowSize.width - pagePadding) {
      left = windowSize.width - pagePadding - popover.width
    }
    // if the popover is too wide, adjust the width
    if (left < pagePadding) {
      targetWidth = windowSize.width - pagePadding * 2
      left = pagePadding
    }

    if (alignment == 'free') {
      arrowStyle = {
        top: placement === 'top' ? '100%' : '0%',
        left: `${container.width / 2 - (left - container.left)}px`,
      }
      if (targetWidth) {
        popoverStyle = {
          width: `${targetWidth}px`,
        }
      }
    }

    position = {
      top,
      left,
    }
  }

  if (validPosition === 'top-free') {
    validPosition = 'top-center'
  } else if (validPosition === 'bottom-free') {
    validPosition = 'bottom-center'
  }

  return {
    position,
    arrowStyle,
    popoverStyle,
    validPosition,
  }
}

/**
 * TODO: This should only need to be done once when the element has been mounted.
 */
export const getElementOffset = (element: HTMLElement) => {
  /**
   * Elements with the transform property acts as a position origin offset for fixed child elements.
   */
  const elementHasTransformProperty = (elem: HTMLElement) => {
    const style = window.getComputedStyle(elem)
    return style.transform && style.transform !== '' && style.transform !== 'none'
  }

  let failSafe = 1000
  let parent: HTMLElement | null = element
  while (failSafe && parent && !elementHasTransformProperty(parent)) {
    parent = parent.parentElement
    if (parent?.tagName.toLowerCase() === 'body') {
      parent = null
      break
    }
    failSafe -= 1
  }
  return parent
    ? parent.getBoundingClientRect()
    : ({
        left: 0,
        top: 0,
      } as DOMRect)
}
